import { getCitationUrl } from '@/utils/jsonTyping';
import { InsightItemDisplay, parseInsightsFromJson } from '@/components/InsightItemDisplay';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogDescription,
} from '@/components/ui/dialog';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { ScrollArea } from '@/components/ui/scroll-area';
import { Card } from '@/components/ui/card';
import {
  Collapsible,
  CollapsibleContent,
  CollapsibleTrigger,
} from '@/components/ui/collapsible';
import {
  Carousel,
  CarouselContent,
  CarouselItem,
} from '@/components/ui/carousel';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from '@/components/ui/dropdown-menu';
import {
  Copy,
  Heart,
  Star,
  ChevronLeft,
  ChevronRight,
  ExternalLink,
  Sparkles,
  CheckCircle2,
  MoreVertical,
  Trash2,
  FileEdit,
  MonitorPlay,
} from 'lucide-react';
import { useNavigate } from 'react-router-dom';
import { ContentIdea, ViralScoreLevel } from '@/types/dashboard';
import { useFavorites } from '@/hooks/useFavorites';
import { useMarkAsUsed } from '@/hooks/useMarkAsUsed';
import { useState, useEffect } from 'react';
import { cn } from '@/lib/utils';
import type { CarouselApi } from '@/components/ui/carousel';

interface ContentDetailModalProps {
  idea: ContentIdea | null;
  isOpen: boolean;
  onClose: () => void;
  onCopyScript: (content: string) => void;
  onFavoriteToggle?: () => void;
  onUsedToggle?: () => void;
  onTrash?: (id: string, source: 'scraped' | 'user_generated') => void;
}

export const ContentDetailModal = ({
  idea,
  isOpen,
  onClose,
  onCopyScript,
  onFavoriteToggle,
  onUsedToggle,
  onTrash
}: ContentDetailModalProps) => {
  const navigate = useNavigate();
  const { toggleFavorite, isLoading: isFavoriteLoading } = useFavorites();
  const { toggleUsed, isLoading: isUsedLoading } = useMarkAsUsed();
  
  // Local state for favorite and used status to update UI immediately
  const [localIsFavorited, setLocalIsFavorited] = useState(idea?.is_favorited || false);
  const [localIsUsed, setLocalIsUsed] = useState(idea?.is_used || false);
  
  // Carousel states
  const [hookCarouselApi, setHookCarouselApi] = useState<CarouselApi>();
  const [overlayCarouselApi, setOverlayCarouselApi] = useState<CarouselApi>();
  const [insightsCarouselApi, setInsightsCarouselApi] = useState<CarouselApi>();
  const [improvementsCarouselApi, setImprovementsCarouselApi] = useState<CarouselApi>();
  
  const [hookCurrent, setHookCurrent] = useState(0);
  const [overlayCurrent, setOverlayCurrent] = useState(0);
  const [insightsCurrent, setInsightsCurrent] = useState(0);
  const [improvementsCurrent, setImprovementsCurrent] = useState(0);
  const [isScriptExpanded, setIsScriptExpanded] = useState(false);
  const [isInsightsExpanded, setIsInsightsExpanded] = useState(false);
  const [isImprovementsExpanded, setIsImprovementsExpanded] = useState(false);
  const [mobileDropdownOpen, setMobileDropdownOpen] = useState(false);
  const [desktopDropdownOpen, setDesktopDropdownOpen] = useState(false);

  // Sync local state when modal opens or idea data changes
  useEffect(() => {
    if (isOpen && idea) {
      setLocalIsFavorited(idea.is_favorited || false);
      setLocalIsUsed(idea.is_used || false);
    }
  }, [isOpen, idea?.id, idea?.is_favorited, idea?.is_used]);

  // Ensure carousels layout correctly after dialog opens (mobile Safari fix)
  useEffect(() => {
    if (!isOpen) return;
    const raf = window.requestAnimationFrame(() => {
      hookCarouselApi?.reInit();
      overlayCarouselApi?.reInit();
      insightsCarouselApi?.reInit();
      improvementsCarouselApi?.reInit();
      hookCarouselApi?.scrollTo(hookCurrent, true);
      overlayCarouselApi?.scrollTo(overlayCurrent, true);
      insightsCarouselApi?.scrollTo(insightsCurrent, true);
      improvementsCarouselApi?.scrollTo(improvementsCurrent, true);
    });
    return () => window.cancelAnimationFrame(raf);
  }, [isOpen, hookCarouselApi, overlayCarouselApi, insightsCarouselApi, improvementsCarouselApi, hookCurrent, overlayCurrent, insightsCurrent, improvementsCurrent]);

  // Hook carousel tracking
  useEffect(() => {
    if (!hookCarouselApi) return;

    setHookCurrent(hookCarouselApi.selectedScrollSnap());

    hookCarouselApi.on('select', () => {
      setHookCurrent(hookCarouselApi.selectedScrollSnap());
    });
  }, [hookCarouselApi]);

  // Overlay carousel tracking
  useEffect(() => {
    if (!overlayCarouselApi) return;
    
    setOverlayCurrent(overlayCarouselApi.selectedScrollSnap());
    
    overlayCarouselApi.on('select', () => {
      setOverlayCurrent(overlayCarouselApi.selectedScrollSnap());
    });
  }, [overlayCarouselApi]);

  // Insights carousel tracking
  useEffect(() => {
    if (!insightsCarouselApi) return;
    
    setInsightsCurrent(insightsCarouselApi.selectedScrollSnap());
    
    insightsCarouselApi.on('select', () => {
      setInsightsCurrent(insightsCarouselApi.selectedScrollSnap());
    });
  }, [insightsCarouselApi]);

  // Improvements carousel tracking
  useEffect(() => {
    if (!improvementsCarouselApi) return;
    
    setImprovementsCurrent(improvementsCarouselApi.selectedScrollSnap());
    
    improvementsCarouselApi.on('select', () => {
      setImprovementsCurrent(improvementsCarouselApi.selectedScrollSnap());
    });
  }, [improvementsCarouselApi]);

  if (!idea) return null;

  // Determine table name based on source
  const getTableName = (): 'content_ideas' | 'user_generated_content' => {
    return idea.source === 'user_generated' ? 'user_generated_content' : 'content_ideas';
  };

  const handleNavigateToEditor = () => {
    onClose();
    navigate(`/edytor?id=${idea.id}&table=${getTableName()}`);
  };

  const handleNavigateToPrompter = () => {
    onClose();
    navigate(`/prompter?id=${idea.id}&table=${getTableName()}`);
  };

  // Safely get array values from potentially non-array fields (handles Json type)
  const getArrayValue = (value: unknown): string[] => {
    if (!value) return [];
    if (Array.isArray(value)) {
      return value.map(item => String(item));
    }
    if (typeof value === 'string') {
      try {
        const parsed = JSON.parse(value);
        if (Array.isArray(parsed)) return parsed.map(item => String(item));
      } catch {
        return [value];
      }
    }
    return [];
  };

  // Parse improvements_applied field (can be string, array, or JSON)
  const parseImprovements = (improvements: unknown): string[] => {
    if (!improvements) return [];
    
    // If already an array
    if (Array.isArray(improvements)) {
      return improvements.map(imp => String(imp));
    }
    
    // If it's a string
    if (typeof improvements === 'string') {
      const trimmed = improvements.trim();
      
      // Try to parse as JSON array
      if (trimmed.startsWith('[')) {
        try {
          const parsed = JSON.parse(trimmed);
          if (Array.isArray(parsed)) {
            return parsed.map(imp => String(imp));
          }
        } catch (e) {
          // If JSON parsing fails, treat as single string
          return [trimmed];
        }
      }
      
      // Single string improvement
      return [trimmed];
    }
    
    return [];
  };

  const parsePerplexityInsights = (data: unknown) => parseInsightsFromJson(data);

  const getCitations = (data: unknown): string[] => {
    if (!data) return [];
    let parsed = data;
    if (typeof data === 'string') {
      try { parsed = JSON.parse(data); } catch { return []; }
    }
    if (parsed && typeof parsed === 'object' && 'citations' in parsed) {
      const citations = (parsed as { citations: unknown }).citations;
      if (Array.isArray(citations)) return citations.map(String);
    }
    return [];
  };

  const altHooks = getArrayValue(idea.alt_hooks);
  const textOverlays = getArrayValue(idea.text_overlays);
  const improvementsApplied = parseImprovements(idea.improvements_applied);
  const perplexityInsights = parsePerplexityInsights(idea.perplexity_insights);
  const citations = getCitations(idea.perplexity_insights);

  // Parse content if it's JSON and extract script field
  const getScriptContent = (content: string | null): string => {
    if (!content) return 'No script content available';
    
    // Helper to normalize literal \n to actual newlines
    const normalizeNewlines = (text: string): string => {
      return text
        .replace(/<br\s*\/?>/gi, '\n')
        .replace(/\\n\\n/g, '\n\n')
        .replace(/\\n/g, '\n')
        .replace(/\n{3,}/g, '\n\n')
        .trim();
    };
    
    try {
      const parsed = JSON.parse(content);
      const script = parsed.script || content;
      return normalizeNewlines(script);
    } catch {
      return normalizeNewlines(content);
    }
  };

  const scriptContent = getScriptContent(idea.edited_content || idea.content);

  const getViralScoreLevel = (score: number | null): ViralScoreLevel => {
    if (!score) return 'low';
    if (score >= 70) return 'high';
    if (score >= 40) return 'medium';
    return 'low';
  };

  const getViralScoreColor = (level: ViralScoreLevel) => {
    switch (level) {
      case 'high': return 'bg-emerald-50/90 dark:bg-emerald-950/50 text-emerald-700 dark:text-emerald-300 border-emerald-300 dark:border-emerald-700';
      case 'medium': return 'bg-amber-50/90 dark:bg-amber-950/50 text-amber-700 dark:text-amber-300 border-amber-300 dark:border-amber-700';
      case 'low': return 'bg-slate-50/90 dark:bg-slate-950/50 text-slate-700 dark:text-slate-300 border-slate-300 dark:border-slate-700';
    }
  };

  const viralLevel = getViralScoreLevel(idea.viral_score);

  const handleFavoriteToggle = async () => {
    // Update UI immediately
    setLocalIsFavorited(!localIsFavorited);
    
    const success = await toggleFavorite(idea.id, localIsFavorited, idea.source);
    if (success && onFavoriteToggle) {
      onFavoriteToggle();
    } else if (!success) {
      // Revert on error
      setLocalIsFavorited(localIsFavorited);
    }
  };

  const handleUsedToggle = async () => {
    // Update UI immediately
    setLocalIsUsed(!localIsUsed);
    
    const success = await toggleUsed(idea.id, localIsUsed, idea.source);
    if (success && onUsedToggle) {
      onUsedToggle();
    } else if (!success) {
      // Revert on error
      setLocalIsUsed(localIsUsed);
    }
  };

  // Use shared citation URL resolver — imported from jsonTyping

  const getVideoUrl = () => {
    return idea.source_competitor?.video_url || `https://instagram.com/${idea.source_competitor?.username?.replace('@', '') || ''}`;
  };

  return (
    <Dialog open={isOpen} onOpenChange={onClose}>
      <DialogContent className="max-w-[95vw] md:max-w-[90vw] lg:max-w-[1600px] xl:max-w-[1800px] h-[90dvh] md:h-[90vh] max-h-[90dvh] p-0 gap-0 overflow-hidden">
        <DialogHeader className="sr-only">
          <DialogTitle className="sr-only">Szczegóły pomysłu</DialogTitle>
          <DialogDescription className="sr-only">{idea.title}</DialogDescription>
        </DialogHeader>
        <div className="flex flex-col md:flex-row h-full min-h-0 relative w-full overflow-hidden">
          {/* Custom styled close button */}
          <button
            onClick={onClose}
            className="absolute top-3 right-3 z-50 h-8 w-8 md:h-9 md:w-9 rounded-full bg-gradient-to-br from-muted to-muted/80 hover:from-destructive/20 hover:to-destructive/10 border border-border hover:border-destructive/30 flex items-center justify-center transition-all duration-200 hover:scale-105 shadow-lg"
            aria-label="Close"
          >
            <svg className="w-4 h-4 text-foreground" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
            </svg>
          </button>
          
          {/* Mobile Layout - Vertical Stack */}
          <div className="md:hidden w-full max-w-full overflow-x-hidden h-full">
            <ScrollArea className="h-full w-full max-w-full">
              <div className="p-3 pt-4 space-y-3 w-full max-w-full overflow-x-hidden box-border">
                {/* Viral Score Badge - Top Left */}
                {idea.viral_score && (
                  <div className="flex justify-start mb-2">
                    <div className={cn(
                      "px-2.5 py-1.5 rounded-lg border shadow-sm",
                      getViralScoreColor(viralLevel)
                    )}>
                      <div className="flex items-center gap-1">
                        <Star className="h-3 w-3 fill-current" />
                        <span className="text-sm font-bold">{idea.viral_score}</span>
                        <span className="text-xs opacity-70">/100</span>
                      </div>
                    </div>
                  </div>
                )}

                {/* Title - Full Width with extra top padding when no viral score */}
                <div className={cn("w-full max-w-full overflow-hidden pr-10", !idea.viral_score && "pt-6")}>
                  <h2 className="text-xl font-bold text-foreground break-words leading-tight mb-2">
                    {idea.title}
                  </h2>
                  {idea.description && (
                    <p className="text-sm text-muted-foreground break-words max-w-full">
                      {idea.description}
                    </p>
                  )}
                </div>

                {/* Hook Section */}
                {altHooks.length > 0 && (
                  <div className="p-2.5 rounded-xl bg-gradient-to-br from-blue-500/10 to-blue-500/5 border border-blue-500/20 w-full max-w-full overflow-hidden">
                    <div className="flex items-center gap-1.5 mb-2">
                      <span className="text-sm">🎣</span>
                      <h3 className="text-xs font-bold text-foreground">Hook</h3>
                    </div>
                    
                    <div className="w-full max-w-full mb-2">
                      <div className="p-2 rounded-lg bg-background/80 border border-blue-500/10 w-full max-w-full overflow-hidden">
                        <p className="text-sm text-foreground leading-relaxed break-words max-w-full">{altHooks[hookCurrent]}</p>
                      </div>
                    </div>

                    <div className="flex items-center justify-center gap-3">
                      <Button
                        variant="outline"
                        size="sm"
                        className="pointer-events-auto h-10 w-10 rounded-full bg-blue-500/20 border-blue-500/40 hover:bg-blue-500 hover:text-white active:bg-blue-600 disabled:opacity-40 p-0 touch-manipulation"
                        onClick={() => {
                          const next = Math.max(hookCurrent - 1, 0);
                          setHookCurrent(next);
                          hookCarouselApi?.scrollTo(next, true);
                        }}
                        disabled={hookCurrent === 0}
                      >
                        <ChevronLeft className="h-5 w-5" />
                      </Button>
                      <span className="text-sm text-foreground font-medium min-w-[3rem] text-center">
                        {hookCurrent + 1}/{altHooks.length}
                      </span>
                      <Button
                        variant="outline"
                        size="sm"
                        className="pointer-events-auto h-10 w-10 rounded-full bg-blue-500/20 border-blue-500/40 hover:bg-blue-500 hover:text-white active:bg-blue-600 disabled:opacity-40 p-0 touch-manipulation"
                        onClick={() => {
                          const last = altHooks.length - 1;
                          const next = Math.min(hookCurrent + 1, last);
                          setHookCurrent(next);
                          hookCarouselApi?.scrollTo(next, true);
                        }}
                        disabled={hookCurrent === altHooks.length - 1}
                      >
                        <ChevronRight className="h-5 w-5" />
                      </Button>
                    </div>
                  </div>
                )}

                {/* Napisy Overlay Section */}
                {textOverlays.length > 0 && (
                  <div className="p-2.5 rounded-xl bg-gradient-to-br from-orange-500/10 to-orange-500/5 border border-orange-500/20 w-full max-w-full overflow-hidden">
                    <div className="flex items-center gap-1.5 mb-2">
                      <span className="text-sm">📝</span>
                      <h3 className="text-xs font-bold text-foreground">Nakładki tekstowe</h3>
                    </div>
                    
                    <div className="w-full max-w-full mb-2">
                      <div className="p-2 rounded-lg bg-background/80 border border-orange-500/10 w-full max-w-full overflow-hidden">
                        <p className="text-sm text-foreground leading-relaxed break-words max-w-full">{textOverlays[overlayCurrent]}</p>
                      </div>
                    </div>

                    <div className="flex items-center justify-center gap-3">
                      <Button
                        variant="outline"
                        size="sm"
                        className="pointer-events-auto h-10 w-10 rounded-full bg-orange-500/20 border-orange-500/40 hover:bg-orange-500 hover:text-white active:bg-orange-600 disabled:opacity-40 p-0 touch-manipulation"
                        onClick={() => {
                          const next = Math.max(overlayCurrent - 1, 0);
                          setOverlayCurrent(next);
                          overlayCarouselApi?.scrollTo(next, true);
                        }}
                        disabled={overlayCurrent === 0}
                      >
                        <ChevronLeft className="h-5 w-5" />
                      </Button>
                      <span className="text-sm text-foreground font-medium min-w-[3rem] text-center">
                        {overlayCurrent + 1}/{textOverlays.length}
                      </span>
                      <Button
                        variant="outline"
                        size="sm"
                        className="pointer-events-auto h-10 w-10 rounded-full bg-orange-500/20 border-orange-500/40 hover:bg-orange-500 hover:text-white active:bg-orange-600 disabled:opacity-40 p-0 touch-manipulation"
                        onClick={() => {
                          const last = textOverlays.length - 1;
                          const next = Math.min(overlayCurrent + 1, last);
                          setOverlayCurrent(next);
                          overlayCarouselApi?.scrollTo(next, true);
                        }}
                        disabled={overlayCurrent === textOverlays.length - 1}
                      >
                        <ChevronRight className="h-5 w-5" />
                      </Button>
                    </div>
                  </div>
                )}

                {/* Scenariusz - Collapsible */}
                <Collapsible open={isScriptExpanded} onOpenChange={setIsScriptExpanded}>
                  <div className="rounded-xl bg-gradient-to-br from-primary/10 via-primary/5 to-transparent border-2 border-primary/20 w-full max-w-full overflow-hidden">
                    <CollapsibleTrigger asChild>
                      <button className="w-full max-w-full p-3 flex flex-col gap-2 hover:bg-primary/5 transition-colors text-left">
                        <div className="flex items-center justify-between w-full">
                          <div className="flex items-center gap-2">
                            <span className="text-base">🎬</span>
                            <h3 className="text-sm font-bold text-foreground">Scenariusz</h3>
                          </div>
                          <div className="flex items-center gap-1 bg-primary/20 px-2 py-1 rounded-full">
                            <span className="text-xs font-medium text-primary">
                              {isScriptExpanded ? 'Zwiń' : 'Rozwiń'}
                            </span>
                            <ChevronRight className={cn(
                              "h-5 w-5 text-primary transition-transform flex-shrink-0",
                              isScriptExpanded && "rotate-90"
                            )} />
                          </div>
                        </div>
                        {/* Preview sentences when collapsed - preserve formatting */}
                        {!isScriptExpanded && scriptContent && (
                          <p className="text-xs text-muted-foreground line-clamp-3 w-full whitespace-pre-wrap">
                            {scriptContent}
                          </p>
                        )}
                      </button>
                    </CollapsibleTrigger>
                    <CollapsibleContent>
                      <div className="px-2.5 pb-2.5 max-w-full space-y-2">
                        {/* Action buttons */}
                        <div className="flex items-center gap-1.5 w-full">
                          <Button onClick={() => onCopyScript(scriptContent)} className="flex-1 min-w-0 px-2 h-8 text-xs border-border/50 hover:bg-muted" variant="outline" size="sm">
                            <Copy className="h-3.5 w-3.5 mr-1.5 flex-shrink-0" />
                            <span className="truncate">Skopiuj</span>
                          </Button>
                          <Button onClick={handleNavigateToEditor} className="flex-1 min-w-0 px-2 h-8 text-xs border-border/50 hover:bg-muted" variant="outline" size="sm">
                            <FileEdit className="h-3.5 w-3.5 mr-1.5 flex-shrink-0" />
                            <span className="truncate">Edytuj</span>
                          </Button>
                          <Button onClick={handleNavigateToPrompter} className="flex-1 min-w-0 px-2 h-8 text-xs bg-primary/10 border-primary/30 text-primary hover:bg-primary/20" variant="outline" size="sm">
                            <MonitorPlay className="h-3.5 w-3.5 mr-1.5 flex-shrink-0" />
                            <span className="truncate">Prompter</span>
                          </Button>
                        </div>
                        <div className="p-2.5 rounded-lg bg-background/50 max-h-[300px] overflow-y-auto w-full max-w-full overflow-x-hidden">
                          <p className="text-sm leading-relaxed text-foreground whitespace-pre-wrap break-words max-w-full">
                            {scriptContent}
                          </p>
                        </div>
                      </div>
                    </CollapsibleContent>
                  </div>
                </Collapsible>

                {/* Perplexity Insights Section - Collapsible on mobile */}
                {perplexityInsights.length > 0 && (
                  <Collapsible open={isInsightsExpanded} onOpenChange={setIsInsightsExpanded}>
                    <div className="rounded-xl bg-gradient-to-br from-green-500/10 to-green-500/5 border border-green-500/20 w-full max-w-full overflow-hidden">
                      <CollapsibleTrigger asChild>
                        <button className="w-full max-w-full p-3 flex items-center justify-between hover:bg-green-500/5 transition-colors">
                          <div className="flex items-center gap-1.5">
                            <span className="text-sm">💡</span>
                            <h3 className="text-xs font-bold text-foreground">Ciekawostki</h3>
                          </div>
                          <div className="flex items-center gap-1 bg-green-500/20 px-2 py-1 rounded-full">
                            <span className="text-xs font-medium text-green-600 dark:text-green-400">
                              {isInsightsExpanded ? 'Zwiń' : 'Rozwiń'}
                            </span>
                            <ChevronRight className={cn(
                              "h-5 w-5 text-green-600 dark:text-green-400 transition-transform flex-shrink-0",
                              isInsightsExpanded && "rotate-90"
                            )} />
                          </div>
                        </button>
                      </CollapsibleTrigger>
                      <CollapsibleContent>
                        <div className="px-2.5 pb-2.5 max-w-full space-y-2">
                          {(() => {
                            const insight = perplexityInsights[insightsCurrent];
                            const citationUrl = getCitationUrl(insight.source, citations);
                            return (
                              <div className="max-h-[220px] overflow-y-auto w-full max-w-full overflow-x-hidden">
                                <div className="p-2.5 rounded-lg bg-background/80 border border-green-500/10 max-w-full overflow-hidden">
                                  <InsightItemDisplay insight={insight} fallbackCitationUrl={citationUrl} compact />
                                </div>
                              </div>
                            );
                          })()}

                          <div className="flex items-center justify-center gap-3">
                            <Button
                              variant="outline"
                              size="sm"
                              className="pointer-events-auto h-10 w-10 rounded-full bg-green-500/20 border-green-500/40 hover:bg-green-500 hover:text-white active:bg-green-600 disabled:opacity-40 p-0 touch-manipulation"
                              onClick={() => {
                                const next = Math.max(insightsCurrent - 1, 0);
                                setInsightsCurrent(next);
                                insightsCarouselApi?.scrollTo(next, true);
                              }}
                              disabled={insightsCurrent === 0}
                            >
                              <ChevronLeft className="h-5 w-5" />
                            </Button>
                            <span className="text-sm text-foreground font-medium min-w-[3rem] text-center">
                              {insightsCurrent + 1}/{perplexityInsights.length}
                            </span>
                            <Button
                              variant="outline"
                              size="sm"
                              className="pointer-events-auto h-10 w-10 rounded-full bg-green-500/20 border-green-500/40 hover:bg-green-500 hover:text-white active:bg-green-600 disabled:opacity-40 p-0 touch-manipulation"
                              onClick={() => {
                                const last = perplexityInsights.length - 1;
                                const next = Math.min(insightsCurrent + 1, last);
                                setInsightsCurrent(next);
                                insightsCarouselApi?.scrollTo(next, true);
                              }}
                              disabled={insightsCurrent === perplexityInsights.length - 1}
                            >
                              <ChevronRight className="h-5 w-5" />
                            </Button>
                          </div>
                        </div>
                      </CollapsibleContent>
                    </div>
                  </Collapsible>
                )}

                {/* Improvements Applied Section - Collapsible on mobile */}
                 {improvementsApplied.length > 0 && (
                  <Collapsible open={isImprovementsExpanded} onOpenChange={setIsImprovementsExpanded}>
                    <div className="rounded-xl bg-gradient-to-br from-blue-500/10 to-blue-500/5 border border-blue-500/20 w-full max-w-full overflow-hidden">
                      <CollapsibleTrigger asChild>
                        <button className="w-full max-w-full p-3 flex items-center justify-between hover:bg-blue-500/5 transition-colors">
                          <div className="flex items-center gap-1.5">
                            <Sparkles className="h-3.5 w-3.5 text-blue-500" />
                            <h3 className="text-xs font-bold text-foreground">Zastosowane ulepszenia</h3>
                          </div>
                          <div className="flex items-center gap-1 bg-blue-500/20 px-2 py-1 rounded-full">
                            <span className="text-xs font-medium text-blue-600 dark:text-blue-400">
                              {isImprovementsExpanded ? 'Zwiń' : 'Rozwiń'}
                            </span>
                            <ChevronRight className={cn(
                              "h-5 w-5 text-blue-600 dark:text-blue-400 transition-transform flex-shrink-0",
                              isImprovementsExpanded && "rotate-90"
                            )} />
                          </div>
                        </button>
                      </CollapsibleTrigger>
                      <CollapsibleContent>
                        <div className="px-2.5 pb-2.5 max-w-full space-y-2">
                          <Carousel
                            setApi={setImprovementsCarouselApi}
                            className="w-full"
                            opts={{ 
                              loop: false,
                              align: "start",
                              watchDrag: false,
                            }}
                          >
                            <CarouselContent>
                              {improvementsApplied.map((improvement, index) => (
                                <CarouselItem key={index}>
                                  <div className="p-2 rounded-lg bg-background/80 border border-blue-500/10 flex items-start gap-2">
                                    <CheckCircle2 className="h-3.5 w-3.5 text-blue-500 flex-shrink-0 mt-0.5" />
                                    <p className="text-xs text-foreground/90 leading-relaxed">{improvement}</p>
                                  </div>
                                </CarouselItem>
                              ))}
                            </CarouselContent>
                          </Carousel>

                          <div className="flex items-center justify-center gap-3">
                            <Button
                              variant="outline"
                              size="sm"
                              className="pointer-events-auto h-10 w-10 rounded-full bg-blue-500/20 border-blue-500/40 hover:bg-blue-500 hover:text-white active:bg-blue-600 disabled:opacity-40 p-0 touch-manipulation"
                              onClick={() => {
                                const next = Math.max(improvementsCurrent - 1, 0);
                                setImprovementsCurrent(next);
                                improvementsCarouselApi?.scrollTo(next, true);
                              }}
                              disabled={improvementsCurrent === 0}
                            >
                              <ChevronLeft className="h-5 w-5" />
                            </Button>
                            <span className="text-sm text-foreground font-medium min-w-[3rem] text-center">
                              {improvementsCurrent + 1}/{improvementsApplied.length}
                            </span>
                            <Button
                              variant="outline"
                              size="sm"
                              className="pointer-events-auto h-10 w-10 rounded-full bg-blue-500/20 border-blue-500/40 hover:bg-blue-500 hover:text-white active:bg-blue-600 disabled:opacity-40 p-0 touch-manipulation"
                              onClick={() => {
                                const last = improvementsApplied.length - 1;
                                const next = Math.min(improvementsCurrent + 1, last);
                                setImprovementsCurrent(next);
                                improvementsCarouselApi?.scrollTo(next, true);
                              }}
                              disabled={improvementsCurrent >= improvementsApplied.length - 1}
                            >
                              <ChevronRight className="h-5 w-5" />
                            </Button>
                          </div>
                        </div>
                      </CollapsibleContent>
                    </div>
                  </Collapsible>
                 )}

                {/* Action Buttons */}
                <div className="sticky bottom-0 bg-background/95 backdrop-blur-sm border-t pt-3 pb-2 w-full max-w-full">
                  <div className="flex gap-2 w-full max-w-full">
                    <Button
                      className="flex-1 min-w-0"
                      size="default"
                      onClick={() => onCopyScript(idea.content!)}
                      disabled={!idea.content}
                    >
                      <Copy className="mr-2 h-4 w-4" />
                      Skopiuj
                    </Button>
                    <Button
                      variant="outline"
                      size="default"
                      className={cn("flex-1 min-w-0", localIsUsed && "text-green-500 hover:text-green-600")}
                      onClick={handleUsedToggle}
                      disabled={isUsedLoading}
                    >
                      <CheckCircle2 className={cn(
                        "mr-2 h-4 w-4",
                        localIsUsed && "fill-green-500"
                      )} />
                      Użyty
                    </Button>
                    <Button
                      variant="outline"
                      size="default"
                      className={cn("flex-1 min-w-0", localIsFavorited && "text-red-500 hover:text-red-600")}
                      onClick={handleFavoriteToggle}
                      disabled={isFavoriteLoading}
                    >
                      <Heart className={cn(
                        "mr-2 h-4 w-4",
                        localIsFavorited && "fill-red-500"
                      )} />
                      Ulubione
                    </Button>
                    <DropdownMenu open={mobileDropdownOpen} onOpenChange={setMobileDropdownOpen}>
                      <DropdownMenuTrigger asChild>
                        <Button variant="outline" size="default">
                          <MoreVertical className="h-4 w-4" />
                        </Button>
                      </DropdownMenuTrigger>
                      <DropdownMenuContent align="end" className="bg-popover border border-border">
                        <DropdownMenuItem
                          onClick={() => {
                            const source = idea.source || (idea.source_scraped_content_id ? 'scraped' : 'user_generated');
                            // Close dropdown first
                            setMobileDropdownOpen(false);
                            if (onTrash) {
                              // Delay to ensure dropdown closes, then close modal and trash
                              setTimeout(() => {
                                onClose();
                                setTimeout(() => {
                                  onTrash(idea.id, source);
                                }, 50);
                              }, 50);
                            }
                          }}
                          className="text-destructive focus:text-destructive"
                        >
                          <Trash2 className="h-4 w-4 mr-2" />
                          Przenieś do kosza
                        </DropdownMenuItem>
                      </DropdownMenuContent>
                    </DropdownMenu>
                  </div>
                </div>
              </div>
            </ScrollArea>
          </div>

          {/* Desktop Layout - Original Two Column */}
          <div className="hidden md:grid grid-cols-[1fr_2fr] h-full min-h-0 w-full">
          {/* Left Sidebar - All Sections Stacked */}
          <div className="border-r bg-muted/30 p-4 h-full overflow-y-auto">
            <div className="space-y-3 pr-4 pb-4">
              {/* Hook Section */}
              {altHooks.length > 0 && (
                <div className="p-4 rounded-xl bg-gradient-to-br from-blue-500/10 to-blue-500/5 border border-blue-500/20">
                  <div className="flex items-center gap-2 mb-3">
                    <span className="text-lg">🎣</span>
                    <h3 className="text-sm font-bold text-foreground">Hook</h3>
                  </div>
                  
                  <Carousel setApi={setHookCarouselApi} className="w-full mb-3" opts={{ watchDrag: false }}>
                    <CarouselContent>
                      {altHooks.map((hook, index) => (
                        <CarouselItem key={index}>
                          <div className="p-3 rounded-lg bg-background/80 border border-blue-500/10">
                            <p className="text-sm text-foreground leading-relaxed">{hook}</p>
                          </div>
                        </CarouselItem>
                      ))}
                    </CarouselContent>
                  </Carousel>

                  <div className="flex items-center justify-center gap-3">
                    <Button
                      variant="outline"
                      size="sm"
                      className="h-10 w-10 rounded-full bg-blue-500/20 border-blue-500/40 hover:bg-blue-500 hover:text-white active:bg-blue-600 disabled:opacity-40 p-0 touch-manipulation"
                      onClick={() => hookCarouselApi?.scrollPrev()}
                      disabled={hookCurrent === 0}
                    >
                      <ChevronLeft className="h-5 w-5" />
                    </Button>
                    <span className="text-sm text-foreground font-medium min-w-[3rem] text-center">
                      {hookCurrent + 1}/{altHooks.length}
                    </span>
                    <Button
                      variant="outline"
                      size="sm"
                      className="h-10 w-10 rounded-full bg-blue-500/20 border-blue-500/40 hover:bg-blue-500 hover:text-white active:bg-blue-600 disabled:opacity-40 p-0 touch-manipulation"
                      onClick={() => hookCarouselApi?.scrollNext()}
                      disabled={hookCurrent === altHooks.length - 1}
                    >
                      <ChevronRight className="h-5 w-5" />
                    </Button>
                  </div>
                </div>
              )}

              {/* Napisy Overlay Section */}
              {textOverlays.length > 0 && (
                <div className="p-4 rounded-xl bg-gradient-to-br from-orange-500/10 to-orange-500/5 border border-orange-500/20">
                  <div className="flex items-center gap-2 mb-3">
                    <span className="text-lg">📝</span>
                    <h3 className="text-sm font-bold text-foreground">Nakładki tekstowe</h3>
                  </div>
                  
                  <Carousel setApi={setOverlayCarouselApi} className="w-full mb-3" opts={{ watchDrag: false }}>
                    <CarouselContent>
                      {textOverlays.map((overlay, index) => (
                        <CarouselItem key={index}>
                          <div className="p-3 rounded-lg bg-background/80 border border-orange-500/10">
                            <p className="text-sm text-foreground leading-relaxed">{overlay}</p>
                          </div>
                        </CarouselItem>
                      ))}
                    </CarouselContent>
                  </Carousel>

                  <div className="flex items-center justify-center gap-3">
                    <Button
                      variant="outline"
                      size="sm"
                      className="h-10 w-10 rounded-full bg-orange-500/20 border-orange-500/40 hover:bg-orange-500 hover:text-white active:bg-orange-600 disabled:opacity-40 p-0 touch-manipulation"
                      onClick={() => overlayCarouselApi?.scrollPrev()}
                      disabled={overlayCurrent === 0}
                    >
                      <ChevronLeft className="h-5 w-5" />
                    </Button>
                    <span className="text-sm text-foreground font-medium min-w-[3rem] text-center">
                      {overlayCurrent + 1}/{textOverlays.length}
                    </span>
                    <Button
                      variant="outline"
                      size="sm"
                      className="h-10 w-10 rounded-full bg-orange-500/20 border-orange-500/40 hover:bg-orange-500 hover:text-white active:bg-orange-600 disabled:opacity-40 p-0 touch-manipulation"
                      onClick={() => overlayCarouselApi?.scrollNext()}
                      disabled={overlayCurrent === textOverlays.length - 1}
                    >
                      <ChevronRight className="h-5 w-5" />
                    </Button>
                  </div>
                </div>
              )}

              {/* Perplexity Insights Section */}
              {perplexityInsights.length > 0 && (
                <div className="p-4 rounded-xl bg-gradient-to-br from-green-500/10 to-green-500/5 border border-green-500/20">
                  <div className="flex items-center gap-2 mb-3">
                    <span className="text-lg">💡</span>
                    <h3 className="text-sm font-bold text-foreground">Ciekawostki</h3>
                  </div>
                  
                  <Carousel setApi={setInsightsCarouselApi} className="w-full mb-2" opts={{ watchDrag: false }}>
                    <CarouselContent>
                      {perplexityInsights.map((insight, index) => {
                        const citationUrl = getCitationUrl(insight.source, citations);
                        return (
                          <CarouselItem key={index}>
                            <div className="max-h-[320px] overflow-y-auto scrollbar-thin scrollbar-thumb-green-500/30 scrollbar-track-green-500/5">
                              <div className="p-4 rounded-lg bg-background/80 border border-green-500/10 pr-2">
                                <InsightItemDisplay insight={insight} fallbackCitationUrl={citationUrl} />
                              </div>
                            </div>
                          </CarouselItem>
                        );
                      })}
                    </CarouselContent>
                  </Carousel>

                  <div className="flex items-center justify-center gap-3">
                    <Button
                      variant="outline"
                      size="sm"
                      className="h-10 w-10 rounded-full bg-green-500/20 border-green-500/40 hover:bg-green-500 hover:text-white active:bg-green-600 disabled:opacity-40 p-0 touch-manipulation"
                        onClick={() => {
                          const api = insightsCarouselApi;
                          if (!api) return;
                          const next = Math.max(insightsCurrent - 1, 0);
                          setInsightsCurrent(next);
                          api.scrollTo(next, true);
                        }}
                      disabled={insightsCurrent === 0}
                    >
                      <ChevronLeft className="h-5 w-5" />
                    </Button>
                    <span className="text-sm text-foreground font-medium min-w-[3rem] text-center">
                      {insightsCurrent + 1}/{perplexityInsights.length}
                    </span>
                    <Button
                      variant="outline"
                      size="sm"
                      className="h-10 w-10 rounded-full bg-green-500/20 border-green-500/40 hover:bg-green-500 hover:text-white active:bg-green-600 disabled:opacity-40 p-0 touch-manipulation"
                      onClick={() => insightsCarouselApi?.scrollNext()}
                      disabled={insightsCurrent === perplexityInsights.length - 1}
                    >
                      <ChevronRight className="h-5 w-5" />
                    </Button>
                  </div>
                </div>
              )}

              {/* Improvements Applied Section */}
              {improvementsApplied.length > 0 && (
                <div className="p-4 rounded-xl bg-gradient-to-br from-blue-500/10 to-blue-500/5 border border-blue-500/20">
                  <div className="flex items-center gap-2 mb-3">
                    <Sparkles className="h-4 w-4 text-blue-500" />
                    <h3 className="text-sm font-bold text-foreground">Zastosowane ulepszenia</h3>
                  </div>
                  
                  <Carousel
                    setApi={setImprovementsCarouselApi}
                    className="w-full mb-3"
                    opts={{ 
                      loop: false,
                      align: "start",
                      watchDrag: false,
                    }}
                  >
                    <CarouselContent>
                      {improvementsApplied.map((improvement, index) => (
                        <CarouselItem key={index}>
                          <div className="p-3 rounded-lg bg-background/80 border border-blue-500/10 flex items-start gap-2.5">
                            <CheckCircle2 className="h-4 w-4 text-blue-500 flex-shrink-0 mt-0.5" />
                            <p className="text-sm text-foreground/90 leading-relaxed">{improvement}</p>
                          </div>
                        </CarouselItem>
                      ))}
                    </CarouselContent>
                  </Carousel>

                  <div className="flex items-center justify-center gap-3">
                    <Button
                      variant="outline"
                      size="sm"
                      className="h-10 w-10 rounded-full bg-blue-500/20 border-blue-500/40 hover:bg-blue-500 hover:text-white active:bg-blue-600 disabled:opacity-40 p-0 touch-manipulation"
                      onClick={() => improvementsCarouselApi?.scrollPrev()}
                      disabled={improvementsCurrent === 0}
                    >
                      <ChevronLeft className="h-5 w-5" />
                    </Button>
                    <span className="text-sm text-foreground font-medium min-w-[3rem] text-center">
                      {improvementsCurrent + 1}/{improvementsApplied.length}
                    </span>
                    <Button
                      variant="outline"
                      size="sm"
                      className="h-10 w-10 rounded-full bg-blue-500/20 border-blue-500/40 hover:bg-blue-500 hover:text-white active:bg-blue-600 disabled:opacity-40 p-0 touch-manipulation"
                      onClick={() => improvementsCarouselApi?.scrollNext()}
                      disabled={improvementsCurrent >= improvementsApplied.length - 1}
                    >
                      <ChevronRight className="h-5 w-5" />
                    </Button>
                  </div>
                </div>
              )}
            </div>
          </div>

          {/* Right Main Content - Script */}
          <div className="flex-1 flex flex-col min-w-0 min-h-0 overflow-hidden h-full">
            {/* Header */}
            <div className="p-4 lg:p-6 border-b flex-shrink-0 min-h-0">
              <div className="flex items-start justify-between gap-4 pr-12">
                <div className="flex-1 min-w-0">
                  <h2 className="text-2xl font-bold text-foreground mb-2">
                    {idea.title}
                  </h2>
                  {idea.description && (
                    <p className="text-sm text-muted-foreground">
                      {idea.description}
                    </p>
                  )}
                </div>
                
                {/* Viral score badge - positioned in flow */}
                {idea.viral_score && (
                  <div className="flex-shrink-0">
                    <div className={cn(
                      "relative px-4 py-2.5 rounded-xl border shadow-sm",
                      getViralScoreColor(viralLevel)
                    )}>
                      {/* Content */}
                      <div className="relative flex items-center gap-2">
                        <Star className="h-4 w-4 fill-current" />
                        <div className="flex flex-col">
                          <span className="text-xl font-bold leading-none">{idea.viral_score}</span>
                          <span className="text-xs opacity-70 leading-none">/100</span>
                        </div>
                      </div>
                    </div>
                  </div>
                )}
              </div>
            </div>

            {/* Script Content */}
            <div className="flex-1 p-4 lg:p-6 min-h-0 overflow-hidden">
              <div className="h-full rounded-2xl bg-gradient-to-br from-primary/10 via-primary/5 to-transparent border-2 border-primary/20 shadow-lg p-4 lg:p-8 relative flex flex-col overflow-hidden">
                {/* Decorative elements */}
                <div className="absolute top-0 right-0 w-64 h-64 bg-primary/5 rounded-full blur-3xl -translate-y-1/2 translate-x-1/2 pointer-events-none"></div>
                <div className="absolute bottom-0 left-0 w-48 h-48 bg-primary/5 rounded-full blur-3xl translate-y-1/2 -translate-x-1/2 pointer-events-none"></div>
                
                {/* Content */}
                <div className="relative z-10 flex flex-col h-full min-h-0">
                  <div className="flex items-center justify-between gap-3 mb-6 flex-shrink-0">
                    <div className="flex items-center gap-3">
                      <span className="text-2xl">🎬</span>
                      <h3 className="text-xl font-bold text-foreground">Scenariusz</h3>
                    </div>
                    <div className="flex items-center gap-2">
                      <Button onClick={() => onCopyScript(scriptContent)} className="px-3 h-8 text-xs border-border/50 hover:bg-muted" variant="outline" size="sm">
                        <Copy className="mr-1.5 h-3.5 w-3.5" />
                        Skopiuj
                      </Button>
                      <Button onClick={handleNavigateToEditor} className="px-3 h-8 text-xs border-border/50 hover:bg-muted" variant="outline" size="sm">
                        <FileEdit className="mr-1.5 h-3.5 w-3.5" />
                        Edytuj
                      </Button>
                      <Button onClick={handleNavigateToPrompter} className="px-3 h-8 text-xs bg-primary/10 border-primary/30 text-primary hover:bg-primary/20" variant="outline" size="sm">
                        <MonitorPlay className="mr-1.5 h-3.5 w-3.5" />
                        Prompter
                      </Button>
                    </div>
                  </div>
                  
                  {/* Scrollable content with always visible modern scrollbar */}
                  <div className="flex-1 overflow-y-auto overflow-x-hidden pr-3 scrollbar-custom">
                    <style>{`
                      .scrollbar-custom::-webkit-scrollbar {
                        width: 14px;
                      }
                      .scrollbar-custom::-webkit-scrollbar-track {
                        background: hsl(var(--primary) / 0.2);
                        border-radius: 100px;
                        margin: 8px 0;
                        border: 2px solid hsl(var(--primary) / 0.1);
                      }
                      .scrollbar-custom::-webkit-scrollbar-thumb {
                        background: linear-gradient(180deg, hsl(var(--primary) / 0.8), hsl(var(--primary) / 0.6));
                        border-radius: 100px;
                        border: 3px solid transparent;
                        background-clip: padding-box;
                        box-shadow: 0 0 8px hsl(var(--primary) / 0.4);
                        transition: all 0.3s ease;
                      }
                      .scrollbar-custom::-webkit-scrollbar-thumb:hover {
                        background: linear-gradient(180deg, hsl(var(--primary) / 1), hsl(var(--primary) / 0.8));
                        background-clip: padding-box;
                        box-shadow: 0 0 12px hsl(var(--primary) / 0.6);
                      }
                      .scrollbar-custom::-webkit-scrollbar-thumb:active {
                        background: hsl(var(--primary));
                        background-clip: padding-box;
                        box-shadow: 0 0 16px hsl(var(--primary) / 0.7);
                      }
                      .scrollbar-custom {
                        scrollbar-width: thin;
                        scrollbar-color: hsl(var(--primary) / 0.7) hsl(var(--primary) / 0.2);
                      }
                    `}</style>
                    <p className="text-lg leading-relaxed text-foreground whitespace-pre-wrap pr-2">
                      {scriptContent}
                    </p>
                  </div>
                </div>
              </div>
            </div>

            {/* Bottom Action Bar */}
            <div className="p-4 lg:p-6 border-t bg-muted/30 flex-shrink-0">
              <div className="flex gap-3">
                <Button
                  className="flex-1"
                  size="lg"
                  onClick={() => onCopyScript(idea.content!)}
                  disabled={!idea.content}
                >
                  <Copy className="mr-2 h-4 w-4" />
                  Skopiuj
                </Button>
                <Button
                  variant="outline"
                  size="lg"
                  className={cn("flex-1", localIsUsed && "text-green-500 hover:text-green-600")}
                  onClick={handleUsedToggle}
                  disabled={isUsedLoading}
                >
                  <CheckCircle2 className={cn(
                    "mr-2 h-4 w-4",
                    localIsUsed && "fill-green-500"
                  )} />
                  Oznacz użyty
                </Button>
                <Button
                  variant="outline"
                  size="lg"
                  className={cn("flex-1", localIsFavorited && "text-red-500 hover:text-red-600")}
                  onClick={handleFavoriteToggle}
                  disabled={isFavoriteLoading}
                >
                  <Heart className={cn(
                    "mr-2 h-4 w-4",
                    localIsFavorited && "fill-red-500"
                  )} />
                  Ulubione
                </Button>
                <DropdownMenu open={desktopDropdownOpen} onOpenChange={setDesktopDropdownOpen}>
                  <DropdownMenuTrigger asChild>
                    <Button variant="outline" size="lg">
                      <MoreVertical className="h-4 w-4" />
                    </Button>
                  </DropdownMenuTrigger>
                  <DropdownMenuContent align="end" className="bg-popover border border-border">
                    <DropdownMenuItem
                      onClick={() => {
                        const source = idea.source || (idea.source_scraped_content_id ? 'scraped' : 'user_generated');
                        // Close dropdown first
                        setDesktopDropdownOpen(false);
                        if (onTrash) {
                          // Delay to ensure dropdown closes, then close modal and trash
                          setTimeout(() => {
                            onClose();
                            setTimeout(() => {
                              onTrash(idea.id, source);
                            }, 50);
                          }, 50);
                        }
                      }}
                      className="text-destructive focus:text-destructive"
                    >
                      <Trash2 className="h-4 w-4 mr-2" />
                      Przenieś do kosza
                    </DropdownMenuItem>
                  </DropdownMenuContent>
                </DropdownMenu>
              </div>
              </div>
            </div>
          </div>
          </div>
      </DialogContent>
    </Dialog>
  );
};
